import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, Input, OnChanges, OnInit, ViewChild, inject } from '@angular/core';

import { FavouritesConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { VirtualCompetitionDisplayMode } from '@frontend/sports/common/core/data-access/sport-model';
import { ScrollAdapterComponent } from '@frontend/sports/common/core/feature/scroll-adapter';
import { ScrollSelectedInViewDirective } from '@frontend/sports/common/core/feature/scroll-selected-in-view';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { FavouriteLeagueToggleComponent } from '@frontend/sports/favourites/feature/league-toggle';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';

import { TournamentGroup, VirtualCompetitionModel } from '../competition-list/services/virtual-competition.service';
import { TournamentGroupsParticipantsComponent } from './tournament-groups-participants.component';

@Component({
    selector: 'ms-tournament-groups',
    templateUrl: './tournament-groups.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ScrollAdapterComponent,
        ScrollSelectedInViewDirective,
        TournamentGroupsParticipantsComponent,
        FavouriteLeagueToggleComponent,
        NgTemplateOutlet,
    ],
})
export class TournamentGroupsComponent implements OnInit, OnChanges {
    private timer = inject(TimerService);
    sitecore = inject(Sitecore);
    redirectHelperService = inject(RedirectHelperService);
    private trackingService = inject(TrackingService);
    private favouritesConfig = inject(FavouritesConfig);

    @Input() model: VirtualCompetitionModel;

    @HostBinding('class') className = 'tournament-groups';
    @ViewChild(ScrollAdapterComponent, { static: true }) scrollAdapter: ScrollAdapterComponent;
    active: TournamentGroup | undefined;
    VirtualCompetitionDisplayMode = VirtualCompetitionDisplayMode;
    showFavouritesToggle = this.favouritesConfig.isFavouritesEnabled;

    ngOnInit(): void {
        this.active = this.model.groups && this.model.groups.find((group) => group.active);
    }

    ngOnChanges(changes: ISimpleChanges<TournamentGroupsComponent>): void {
        this.timer.setTimeout(() => this.scrollAdapter.setArrows());
    }

    selected(event: Event, active: TournamentGroup): void {
        event.preventDefault();

        if (this.model.groups.find((group) => group.id === active.id)) {
            this.model.groups.forEach((group) => (group.active = group.id === active.id));
            this.active = active;

            this.trackingService.track('Event.Clicks', {
                [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'Tournaments Groups',
                [trackingConstants.COMPONENT_LABEL_EVENT]: `Tournaments Group: ${active.title}`,
                [trackingConstants.COMPONENT_ACTION_EVENT]: 'Group - Clicked',
                [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
                [trackingConstants.COMPONENT_LOCATION_EVENT]: 'Tournaments Page',
                [trackingConstants.COMPONENT_EVENT_DETAILS]: 'Visitor Clicks on a Tournaments Group',
                [trackingConstants.COMPONENT_URL_CLICKED]: active.url || NOT_APPLICABLE,
            });

            this.redirectHelperService.goToPage(active.url, true);
        }
    }

    trackTabsBy(index: number, group: TournamentGroup): number {
        return group.id;
    }

    getGroupMenuItemId(item: TournamentGroup | undefined): string {
        const itemIndex = !item ? -1 : this.model.groups.findIndex((group) => group.id === item.id);
        const itemId = !item ? 0 : item.id;

        return `${itemId}#${itemIndex}`;
    }
}
